import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Car, Loader2, Users } from "lucide-react";
import { vehicleTypesAPI } from "../lib/api";

export default function VehicleTypesManager() {
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    capacity: 4,
    description: "",
    is_active: true
  });

  useEffect(() => {
    fetchVehicleTypes();
  }, [showInactive]);

  const fetchVehicleTypes = async () => {
    try {
      setLoading(true);
      const response = await vehicleTypesAPI.getAll(showInactive);
      setVehicleTypes(response.data);
    } catch (error) {
      toast.error("Error al cargar tipos de vehículo");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingType(null);
    setFormData({
      name: "",
      code: "",
      capacity: 4,
      description: "",
      is_active: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      code: type.code,
      capacity: type.capacity || 4,
      description: type.description || "",
      is_active: type.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    
    if (!formData.code.trim()) {
      toast.error("El código es obligatorio");
      return;
    }

    // Validate code format (lowercase, no spaces)
    const codeRegex = /^[a-z0-9_]+$/;
    if (!codeRegex.test(formData.code)) {
      toast.error("El código debe ser en minúsculas, sin espacios (use guion bajo)");
      return;
    }

    setSaving(true);
    try {
      if (editingType) {
        // Update - don't send code (it's immutable)
        const { code, ...updateData } = formData;
        await vehicleTypesAPI.update(editingType.id, updateData);
        toast.success("Tipo de vehículo actualizado");
      } else {
        // Create
        await vehicleTypesAPI.create(formData);
        toast.success("Tipo de vehículo creado");
      }
      setDialogOpen(false);
      fetchVehicleTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type) => {
    if (!window.confirm(`¿Desactivar el tipo "${type.name}"? Los contratos existentes no se verán afectados.`)) {
      return;
    }
    
    try {
      await vehicleTypesAPI.delete(type.id);
      toast.success("Tipo de vehículo desactivado");
      fetchVehicleTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al desactivar");
    }
  };

  const handleReactivate = async (type) => {
    try {
      await vehicleTypesAPI.update(type.id, { is_active: true });
      toast.success("Tipo de vehículo reactivado");
      fetchVehicleTypes();
    } catch (error) {
      toast.error("Error al reactivar");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Car className="w-5 h-5" />
              Tipos de Vehículo
            </CardTitle>
            <CardDescription>
              Gestione los tipos de unidades disponibles para contratos y cotizaciones
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="show-inactive"
                checked={showInactive}
                onCheckedChange={setShowInactive}
              />
              <Label htmlFor="show-inactive" className="text-sm">Mostrar inactivos</Label>
            </div>
            <Button onClick={openCreateDialog} data-testid="add-vehicle-type-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Tipo
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : vehicleTypes.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            No hay tipos de vehículo configurados
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Código</TableHead>
                <TableHead className="text-center">Capacidad</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehicleTypes.map((type) => (
                <TableRow key={type.id} className={!type.is_active ? "opacity-50" : ""}>
                  <TableCell className="font-medium">{type.name}</TableCell>
                  <TableCell>
                    <code className="px-2 py-1 bg-slate-100 rounded text-sm">{type.code}</code>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Users className="w-4 h-4 text-slate-400" />
                      {type.capacity}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm max-w-xs truncate">
                    {type.description || "-"}
                  </TableCell>
                  <TableCell className="text-center">
                    {type.is_active ? (
                      <Badge variant="success" className="bg-green-100 text-green-800">Activo</Badge>
                    ) : (
                      <Badge variant="secondary">Inactivo</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(type)}
                        data-testid={`edit-type-${type.code}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {type.is_active ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(type)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-type-${type.code}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReactivate(type)}
                          className="text-green-600 hover:text-green-700"
                        >
                          Reactivar
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingType ? "Editar Tipo de Vehículo" : "Nuevo Tipo de Vehículo"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Sedán Ejecutivo"
                  required
                  data-testid="vehicle-type-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="code">Código *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                  placeholder="Ej: sedan, suv, van_ejecutiva"
                  required
                  disabled={!!editingType}
                  className={editingType ? "bg-slate-100" : ""}
                  data-testid="vehicle-type-code-input"
                />
                <p className="text-xs text-slate-500">
                  Identificador único. Solo minúsculas y guion bajo. {editingType && "(No editable)"}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="capacity">Capacidad de Pasajeros</Label>
                <Input
                  id="capacity"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 4 })}
                  data-testid="vehicle-type-capacity-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción opcional del tipo de vehículo"
                  rows={2}
                  data-testid="vehicle-type-description-input"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Activo</Label>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-vehicle-type-btn">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    "Guardar"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
